import React from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Sparkles, Clock } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

// Persistent banner shown to company users when their subscription is in a
// read-only state (past_due / suspended / canceled) or trialing. Super-admins
// and users with no company never see it.
export default function SubscriptionBanner() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const company = user && user !== false ? user.company : null;
  if (!company) return null;
  if (user.role === "super_admin") return null;

  const { subscriptionStatus, readOnly, daysLeft } = company;
  const isAdmin = user.role === "admin";

  if (readOnly) {
    const label =
      subscriptionStatus === "suspended"
        ? "Your account has been suspended."
        : subscriptionStatus === "canceled"
        ? "Your subscription has been canceled."
        : "Your subscription payment is past due.";
    return (
      <div
        data-testid="subscription-banner-readonly"
        className="flex items-center gap-3 px-5 py-2.5 bg-rose-600 text-white text-[13px]"
      >
        <AlertTriangle className="h-4 w-4 shrink-0" />
        <span className="font-medium">{label}</span>
        <span className="text-rose-100">Your workspace is in read-only mode until billing is resolved.</span>
        {isAdmin && (
          <button
            data-testid="banner-renew-btn"
            onClick={() => navigate("/settings/billing")}
            className="ml-auto shrink-0 px-3 py-1 rounded-md bg-white text-rose-700 text-[12px] font-semibold hover:bg-rose-50"
          >
            Renew now
          </button>
        )}
      </div>
    );
  }

  if (subscriptionStatus === "trialing") {
    // Ramp up urgency in the final stretch of the trial to drive conversion.
    const urgent = daysLeft != null && daysLeft <= 3;
    const daysText = daysLeft != null ? `${daysLeft} day${daysLeft === 1 ? "" : "s"}` : null;
    return (
      <div
        data-testid="subscription-banner-trial"
        className={`flex items-center gap-3 px-5 py-2.5 text-[13px] border-b ${
          urgent
            ? "bg-rose-600 text-white border-rose-700"
            : "bg-amber-50 border-amber-200 text-amber-900"
        }`}
      >
        {urgent ? (
          <Clock className="h-4 w-4 shrink-0 text-white" />
        ) : (
          <Sparkles className="h-4 w-4 shrink-0 text-amber-500" />
        )}
        <span className="font-semibold" data-testid="banner-trial-days">
          {urgent
            ? daysText
              ? `Your free trial ends in ${daysText}`
              : "Your free trial is ending soon"
            : daysText
            ? `Free trial — ${daysText} left`
            : "Free trial active"}
        </span>
        <span className={`hidden sm:inline ${urgent ? "text-rose-100" : "text-amber-700"}`}>
          {urgent
            ? "Add billing now to keep your workspace active."
            : "Add billing anytime to continue after your trial ends."}
        </span>
        {isAdmin && (
          <button
            data-testid="banner-upgrade-btn"
            onClick={() => navigate("/settings/billing")}
            className={`ml-auto shrink-0 px-3 py-1 rounded-md text-[12px] font-semibold transition-colors ${
              urgent
                ? "bg-white text-rose-700 hover:bg-rose-50"
                : "bg-amber-500 text-white hover:bg-amber-600"
            }`}
          >
            {urgent ? "Add billing now" : "Add billing"}
          </button>
        )}
      </div>
    );
  }

  return null;
}
